import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Truck, Car, Settings, CheckCircle, ArrowRight } from 'lucide-react';
import { useServices } from '../hooks/useCMS';
import { useCMS } from '../hooks/useCMS';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ServicesPublic = () => {
  const { services, loading } = useServices();
  const { get } = useCMS('services');
  const [activeCategory, setActiveCategory] = useState('all');
  const [servicePages, setServicePages] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch page info for each service
    const fetchServicePages = async () => {
      const pages = {};
      for (const service of services) {
        try {
          const response = await axios.get(`${API}/pages/service/${service.id}`);
          if (response.data.slug) {
            pages[service.id] = response.data;
          }
        } catch (error) {
          // No page for this service
        }
      }
      setServicePages(pages);
    };
    
    if (services.length > 0) {
      fetchServicePages();
    }
  }, [services]);

  const categories = [
    { id: 'all', label: 'Alle Tjenester', icon: Settings },
    { id: 'tungbil', label: 'Tungbil', icon: Truck },
    { id: 'tilhenger', label: 'Tilhenger', icon: Truck },
    { id: 'personbil', label: 'Personbil', icon: Car }
  ];

  if (loading) {
    return (
      <section id="services" className="py-24 bg-gradient-to-b from-gray-900 via-black to-gray-900">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400">Laster tjenester...</p>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-gray-900 via-black to-gray-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="checkered-bg w-full h-full"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="bg-red-600/20 text-red-400 border-red-600/50 mb-4 px-4 py-1">
            {get('services_badge', 'Våre Tjenester')}
          </Badge>
          <h2 className="text-4xl md:text-6xl font-black mb-6">
            <span className="text-white">{get('services_title_part1', 'Fast prisede')}</span>{' '}
            <span className="bg-gradient-to-r from-red-500 to-yellow-400 bg-clip-text text-transparent">{get('services_title_part2', 'tjenester')}</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            {get('services_subtitle', 'Transparent prising og profesjonell service for alle kjøretøytyper')}
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg shadow-red-500/50 scale-105'
                    : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50 hover:text-white border border-gray-700'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{category.label}</span>
              </button>
            );
          })}
        </div>

        {/* Services Grid - ALL services always in DOM for SEO/AI (3LA compliant) 
            Note: We use CSS transitions instead of removing from DOM to keep content visible to crawlers */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const isVisible = activeCategory === 'all' || service.category === activeCategory;
            return (
              <Card
                key={service.id}
                className={`bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700 backdrop-blur-sm transition-all duration-300 group ${
                  isVisible 
                    ? 'hover:border-red-600/50 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-red-500/20 opacity-100' 
                    : 'opacity-40 grayscale pointer-events-none'
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
                data-category={service.category}
              >
              <CardHeader>
                <div className="flex items-start justify-between mb-2">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <Badge className="bg-red-600/20 text-red-400 border-red-600/50">
                    {service.category === 'tungbil' ? 'Tungbil' : service.category === 'tilhenger' ? 'Tilhenger' : 'Personbil'}
                  </Badge>
                </div>
                <CardTitle className="text-white text-lg group-hover:text-red-400 transition-colors duration-300">
                  {service.title}
                </CardTitle>
                {service.description && (
                  <CardDescription className="text-gray-400 text-sm mt-2">
                    {service.description}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline space-x-2 mb-4">
                  <span className="text-3xl font-black text-white group-hover:text-red-400 transition-colors duration-300">
                    {service.price}
                  </span>
                </div>
                
                {/* Read more button if page exists */}
                {servicePages[service.id] && (
                  <Button
                    onClick={() => navigate(`/tjeneste/${servicePages[service.id].slug}`)}
                    variant="outline"
                    className="w-full border-red-600/50 text-red-400 hover:bg-red-600/20 hover:border-red-600 transition-all group/btn"
                  >
                    <span>Les mer</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                )}
              </CardContent>
            </Card>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-gray-400 mb-4">{get('services_cta_text', 'Ikke funnet det du leter etter?')}</p>
          <a
            href="#contact"
            className="inline-flex items-center space-x-2 text-red-400 hover:text-red-300 font-semibold transition-colors duration-300"
          >
            <span>{get('services_cta_link', 'Kontakt oss for tilpasset tilbud')}</span>
            <span>→</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default ServicesPublic;